import { useEffect, useState } from "react";
import { createScoreEntity, extractDimensions } from "../utils/chartUtils";
import { FormattedScoreChartData, VariationState } from "../types";
import { ScoreChartValues } from "src/components/ScoreChart/ScoreChart";
import { usePuckCompanyScore } from "../../../contexts/PuckCompanyScoreContext";
import { ScoreChartBlockProps } from "../ScoreChartBlock";
import { Score, ScoreVariation, ScoreDimension } from "src/api/client-api/post/scoreSchema";

type ScoreChartData = Partial<Score | ScoreVariation> & { dimensions?: ScoreDimension[] };

export const useScoreChartData = (options: ScoreChartBlockProps) => {
    const { scoreId, variationId: propVariationId } = options;
    const { companyPost, isLoading, setVariation } = usePuckCompanyScore();

    const [localSelectedVariation, setLocalSelectedVariation] = useState<VariationState | null>(null);

    useEffect(() => {
        if (scoreId) {
            const stringVariationId =
                propVariationId !== null && propVariationId !== undefined ? String(propVariationId) : null;

            setLocalSelectedVariation({
                scoreId,
                variationId: stringVariationId,
            });

            const numericalVariationId = stringVariationId ? Number(stringVariationId) : null;
            setVariation(scoreId, numericalVariationId);
        }
    }, [scoreId, propVariationId]);

    const effectiveVariationId =
        propVariationId !== undefined ? propVariationId
        : localSelectedVariation && localSelectedVariation.scoreId === scoreId ?
            localSelectedVariation.variationId !== null ?
                Number(localSelectedVariation.variationId)
            :   null
        :   null;

    const getFormattedData = (): FormattedScoreChartData | null => {
        if (!scoreId) {
            return null;
        }

        const scoreData = companyPost?.scores?.[scoreId || ""];
        const scoreValue = scoreData?.score;
        const scoreUnit = scoreData?.unit;

        const getChartData = (): ScoreChartValues => {
            if (!scoreData) {
                return {
                    values: [],
                    labels: [],
                };
            }

            const extractChartData = (rawData: ScoreChartData): ScoreChartValues => {
                try {
                    const dimensions = rawData.dimensions;
                    const dimensionValues = dimensions?.[0]?.values || {};

                    const values: number[] = [];
                    const labels: string[] = [];

                    Object.values(dimensionValues).forEach((item) => {
                        if (item) {
                            const value = Number(item.score || 0);
                            const label = item.label || "";
                            values.push(value);
                            labels.push(label);
                        }
                    });

                    return { values, labels };
                } catch (e) {
                    return { values: [], labels: [] };
                }
            };

            if (scoreData.variations && effectiveVariationId !== null) {
                const variationData = scoreData.variations.find(
                    (variation) => variation.id === Number(effectiveVariationId),
                );

                if (variationData) {
                    const chartData = extractChartData(variationData);
                    if (chartData.values.length > 0) {
                        return chartData;
                    }
                }
            }

            return extractChartData(scoreData);
        };

        const chartData = getChartData();

        const variation =
            effectiveVariationId && scoreData?.variations ?
                scoreData.variations[Number(effectiveVariationId)]
            :   undefined;

        const variationTotalScore =
            variation && typeof variation === "object" && "score" in variation ? Number(variation.score) : undefined;

        const displayScore =
            variationTotalScore !== undefined ? variationTotalScore
            : scoreValue !== undefined && scoreValue !== null ? Number(scoreValue)
            : undefined;
        const dimensions = extractDimensions(scoreData);
        const selectedScoreEntity = createScoreEntity(scoreData, scoreUnit, effectiveVariationId, dimensions);

        return {
            chartData,
            displayScore,
            selectedScoreEntity,
        };
    };

    return {
        formattedData: getFormattedData(),
        isLoading,
        hasScore: Boolean(scoreId),
    };
};
